import { useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import GridElement from "../components/Nemo/GridElement";
import WallElement from "../components/Nemo/WallElement";
import WallModal from "../components/Nemo/WallModal";
import LandBoundary from "../components/Nemo/LandBoundary";
import AddressModal from "../components/Nemo/KakaoAddressSearchModal";
import { Wall } from "../types";
import { getLots } from "../api";
import { useMutation } from "@tanstack/react-query";
import SelectedLotInfo from "../components/Nemo/SelectedLotInfo";

interface FloorData {
  walls: Wall[];
}

export default function NemoView() {
  const [currentFloor, setCurrentFloor] = useState(1);
  const [maxFloor, setMaxFloor] = useState(1);
  const [selectedWall, setSelectedWall] = useState<number | null>(null);
  const [isWallModalOpen, setIsWallModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<string>("");

  const [selectedLot, setSelectedLot] = useState<any | null>(null);
  const [neighborLots, setNeighborLots] = useState<any[]>([]);
  const [floors, setFloors] = useState<Record<number, FloorData>>({
    1: { walls: [] },
  });
  const landVertices = [
    { x: -20, z: -20 },
    { x: 20, z: -20 },
    { x: 20, z: 20 },
    { x: -20, z: 20 },
  ];
  const handleSegmentClick = (x: number, z: number, rotation: number) => {
    setFloors((prev) => ({
      ...prev,
      [currentFloor]: {
        ...prev[currentFloor],
        walls: [
          ...(prev[currentFloor]?.walls || []),
          { position: [x, 0, z], rotation, type: "normal" },
        ],
      },
    }));
  };

  const handleWallClick = (index: number) => {
    setSelectedWall(index);
    setIsWallModalOpen(true);
  };

  const handleWallTypeSelect = (type: "normal" | "window") => {
    if (selectedWall !== null) {
      setFloors((prev) => ({
        ...prev,
        [currentFloor]: {
          ...prev[currentFloor],
          walls: prev[currentFloor].walls.map((wall, index) =>
            index === selectedWall ? { ...wall, type } : wall
          ),
        },
      }));
    }
    setIsWallModalOpen(false);
    setSelectedWall(null);
  };

  const handleWallDelete = (index: number | null) => {
    if (index !== null) {
      setFloors((prev) => ({
        ...prev,
        [currentFloor]: {
          ...prev[currentFloor],
          walls: prev[currentFloor].walls.filter((_, i) => i !== index),
        },
      }));
    }
    setIsWallModalOpen(false);
    setSelectedWall(null);
  };

  const handleAddressSearch = () => {
    setIsAddressModalOpen(true);
  };

  const lotMutation = useMutation({
    mutationFn: getLots,
    onSuccess: (data) => {
      setSelectedLot(data.lot);
      setNeighborLots(data.neighbors || []);
    },
    onError: (error) => {
      console.error(error);
    },
  });
  const handleAddressSelect = async (data: {
    buildingCode: string;
    roadAddress: string;
    jibunAddress: string;
  }) => {
    console.log(data.buildingCode);
    const fullAddress = data.roadAddress || data.jibunAddress;
    setSelectedAddress(fullAddress);
    setIsAddressModalOpen(false);

    lotMutation.mutate(data.buildingCode.slice(0, 19));
    // const lot = await getLots(data.buildingCode);
    // console.log(lot);
    // TODO: Handle the selected address coordinates if needed
  };

  const handleEstimate = () => {
    // TODO: Implement estimate view
    console.log("Estimate clicked");
  };

  const handleSchedule = () => {
    // TODO: Implement schedule view
    console.log("Schedule clicked");
  };

  const handleRefresh = () => {
    setFloors({
      1: { walls: [] },
    });
  };

  const handleFloorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFloor = parseInt(e.target.value);
    setCurrentFloor(newFloor);

    // Initialize floor data if it doesn't exist
    setFloors((prev) => {
      if (!prev[newFloor]) {
        return {
          ...prev,
          [newFloor]: { walls: [] },
        };
      }
      return prev;
    });
  };

  const handleAddFloor = () => {
    if (maxFloor < 6) {
      const newMaxFloor = maxFloor + 1;
      setMaxFloor(newMaxFloor);
      setFloors((prev) => ({
        ...prev,
        [newMaxFloor]: { walls: [] },
      }));
      if (currentFloor === maxFloor) {
        setCurrentFloor(newMaxFloor);
      }
    }
  };

  const handleRemoveFloor = () => {
    if (maxFloor > 1) {
      const newMaxFloor = maxFloor - 1;
      setMaxFloor(newMaxFloor);
      if (currentFloor > newMaxFloor) {
        setCurrentFloor(newMaxFloor);
      }
    }
  };

  return (
    <div style={{ width: "100%", height: "100vh" }}>
      <Canvas camera={{ position: [20, 20, 20], fov: 50 }}>
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} />
        {selectedLot ? (
          <LandBoundary geometry={selectedLot.geometry} floor={currentFloor} />
        ) : null}
        <GridElement
          size={20}
          step={1}
          onSegmentClick={handleSegmentClick}
          floor={currentFloor}
        />
        {floors[currentFloor]?.walls.map((wall, index) => (
          <WallElement
            key={`${currentFloor}-${index}`}
            position={wall.position}
            rotation={wall.rotation}
            type={wall.type}
            onClick={() => handleWallClick(index)}
            floor={currentFloor}
          />
        ))}
        <OrbitControls />
        <gridHelper args={[20, 20]} />
      </Canvas>
      <SelectedLotInfo lot={selectedLot} />
      <WallModal
        isOpen={isWallModalOpen}
        onClose={() => setIsWallModalOpen(false)}
        onSelectType={handleWallTypeSelect}
        onDelete={() => handleWallDelete(selectedWall)}
      />
      {isAddressModalOpen && (
        <AddressModal
          onClose={() => setIsAddressModalOpen(false)}
          onComplete={handleAddressSelect}
        />
      )}
      <div className="fixed bottom-0 left-0 right-0 bg-white p-3 flex justify-between items-center shadow-[0_-2px_10px_rgba(0,0,0,0.1)] z-[100]">
        <button
          className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-medium transition hover:bg-blue-700 active:bg-blue-800"
          onClick={handleAddressSearch}
        >
          주소검색
        </button>
        <button
          className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-medium transition hover:bg-blue-700 active:bg-blue-800"
          onClick={handleEstimate}
        >
          견적보기
        </button>
        <button
          className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-medium transition hover:bg-blue-700 active:bg-blue-800"
          onClick={handleSchedule}
        >
          일정보기
        </button>
        <button
          className="bg-gray-100 text-gray-800 rounded-lg px-4 py-2 text-sm font-medium transition hover:bg-gray-200 active:bg-gray-300"
          onClick={handleRefresh}
        >
          Refresh
        </button>
      </div>
      <div className="fixed top-5 right-5 bg-white p-4 rounded-xl shadow-lg flex flex-col items-center gap-3 z-[100]">
        <div className="flex flex-col items-center gap-2 relative">
          <div className="text-sm font-medium text-gray-800">{maxFloor}</div>
          <input
            type="range"
            min="1"
            max={maxFloor}
            value={currentFloor}
            onChange={handleFloorChange}
            className="w-2 h-52 accent-blue-600"
            style={{
              writingMode: "bt-lr" as any,
              WebkitAppearance: "slider-vertical",
            }}
          />
          <div className="text-sm font-medium text-gray-800">1</div>
          <div className="absolute left-5 top-1/2 -translate-y-1/2 text-sm font-medium text-blue-600 bg-white px-2 py-0.5 rounded shadow">
            {currentFloor}F
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full">
          <button
            className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-medium transition w-full hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={handleAddFloor}
            disabled={maxFloor >= 6}
          >
            층수추가
          </button>
          <button
            className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-medium transition w-full hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={handleRemoveFloor}
            disabled={maxFloor <= 1}
          >
            층수빼기
          </button>
        </div>
      </div>
    </div>
  );
}
